import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import { userKeys } from './useUsers';
import { enrollmentKeys } from './useEnrollments';
import type { Evaluation, EvaluationQuestion, EvaluationOption } from '@lms/shared/types';

export const evaluationKeys = {
  all: ['evaluations'] as const,
  lists: () => [...evaluationKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...evaluationKeys.lists(), params] as const,
  details: () => [...evaluationKeys.all, 'detail'] as const,
  detail: (id: string) => [...evaluationKeys.details(), id] as const,
  participantProgress: (userId: string, programId: string) =>
    [...evaluationKeys.all, 'participant-progress', userId, programId] as const,
};

export function useEvaluations(params?: {
  subjectId?: string;
  sessionId?: string;
  materialId?: string;
  flowId?: string;
  levelId?: string;
  programId?: string;
  userId?: string;
}) {
  return useQuery({
    queryKey: evaluationKeys.list(params || {}),
    queryFn: () => apiClient.evaluations.list(params),
  });
}

export function useEvaluationDetail(id: string) {
  return useQuery({
    queryKey: evaluationKeys.detail(id),
    queryFn: () => apiClient.evaluations.get(id),
    enabled: !!id,
  });
}

export function useParticipantEvaluationProgress(
  userId: string,
  programId: string,
  enabled = true
) {
  return useQuery({
    queryKey: evaluationKeys.participantProgress(userId, programId),
    queryFn: () => apiClient.evaluations.getParticipantProgress(userId, programId),
    enabled: enabled && Boolean(userId) && Boolean(programId),
  });
}

export function useCreateEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Evaluation>) => apiClient.evaluations.create(data),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
      // FIX: invalidate program detail so linked evaluations appear immediately
      if (data.programId) {
        void queryClient.invalidateQueries({
          queryKey: programKeys.detail(data.programId),
        });
      }
    },
  });
}

export function useUpdateEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Evaluation>) =>
      apiClient.evaluations.update(id, data),
    onSuccess: (result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(id) });
      // FIX: invalidate program detail to reflect link changes
      const evalWithProgram = result as Evaluation;
      if (evalWithProgram.programId) {
        void queryClient.invalidateQueries({
          queryKey: programKeys.detail(evalWithProgram.programId),
        });
      }
    },
  });
}

export function useDeleteEvaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.evaluations.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
    },
  });
}

export function useAddEvaluationQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      evaluationId,
      ...data
    }: {
      evaluationId: string;
    } & Omit<EvaluationQuestion, 'id' | 'evaluationId' | 'options'> & {
        options?: Array<Omit<EvaluationOption, 'id' | 'questionId'>>;
      }) =>
      apiClient.evaluations.addQuestion(
        evaluationId,
        data as unknown as Partial<EvaluationQuestion>
      ),
    onSuccess: (_, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useBulkAddEvaluationQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      evaluationId,
      questions,
    }: {
      evaluationId: string;
      // Caller-side shape. `order` is optional here because upstream forms
      // (e.g. `ParsedQuestion[]` from `parseQuestionsFromText`) carry a
      // UI-level `order` for preview numbering. The server's
      // `BulkCreateQuestionDto` (apps/api `evaluation.dto.ts`) deliberately
      // omits `order` because the server assigns `order = currentMax + 1..N`,
      // and PR #112 wired `forbidNonWhitelisted: true` on the global
      // ValidationPipe so any caller that forwards `order` here 400s. We
      // strip it at this single boundary so callers keep their richer
      // UI-level type without leaking the field to the strict DTO. Per-option
      // `order` is preserved: `CreateQuestionOptionDto` still requires it.
      questions: Array<{
        question: string;
        type: string;
        order?: number;
        explanation?: string;
        points?: number;
        options?: { text: string; isCorrect: boolean; order: number }[];
      }>;
    }) =>
      apiClient.evaluations.bulkAddQuestions(evaluationId, {
        questions: questions.map(question => ({
          question: question.question,
          type: question.type,
          explanation: question.explanation,
          points: question.points,
          options: question.options,
        })),
      }),
    onSuccess: (_, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useUpdateEvaluationQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      evaluationId,
      ...data
    }: {
      id: string;
      evaluationId: string;
    } & Partial<EvaluationQuestion>) =>
      apiClient.evaluations.updateQuestion(id, data as unknown as Partial<EvaluationQuestion>),
    onSuccess: (_, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useDeleteEvaluationQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; evaluationId: string }) =>
      apiClient.evaluations.deleteQuestion(id),
    onSuccess: (_, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useLinkEvaluationToMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ evaluationId, materialId }: { evaluationId: string; materialId: string }) =>
      apiClient.evaluations.update(evaluationId, { materialId }),
    onSuccess: (_result, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      // broad invalidation since we don't have programId context here
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
    },
  });
}

export function useUnlinkEvaluationFromMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ evaluationId }: { evaluationId: string }) =>
      apiClient.evaluations.update(evaluationId, {
        materialId: null,
      } as unknown as Partial<Evaluation>),
    onSuccess: (_result, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.all });
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
    },
  });
}

export function useImportEvaluationQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      evaluationId,
      sourceEvaluationId,
      questionIds,
    }: {
      evaluationId: string;
      sourceEvaluationId: string;
      questionIds?: string[];
    }) =>
      apiClient.evaluations.importQuestions(evaluationId, {
        sourceEvaluationId,
        questionIds,
      }),
    onSuccess: (_, { evaluationId }) => {
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useResetAttempts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      evaluationId,
      userId,
      reason,
    }: {
      evaluationId: string;
      userId: string;
      reason: string;
    }) =>
      apiClient.evaluations.resetAttempts(evaluationId, {
        userId,
        reason,
        confirmation: 'RESET',
      }),
    onSuccess: (_, { evaluationId, userId }) => {
      void queryClient.invalidateQueries({ queryKey: ['prior-learning'] });
      void queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      void queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === evaluationKeys.all[0] &&
          query.queryKey[1] === 'participant-progress' &&
          query.queryKey[2] === userId,
      });
    },
  });
}

export function useResetProgramAttempts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programId,
      userId,
      reason,
    }: {
      programId: string;
      userId: string;
      reason: string;
    }) =>
      apiClient.evaluations.resetProgramAttempts(programId, userId, {
        reason,
        confirmation: 'RESET',
      }),
    onSuccess: (_, { programId, userId }) => {
      void queryClient.invalidateQueries({ queryKey: ['prior-learning'] });
      void queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: programKeys.all });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({
        queryKey: evaluationKeys.participantProgress(userId, programId),
      });
    },
  });
}
